// =============================================================
// KohakuPage — Kohaku (amber) tab: payslip, 7-day chart,
// joke conversions, and health gauges.
// =============================================================

import { useEffect, useState, useCallback } from 'react'
import '../native-usage.css'
import { authFetch } from '../lib/auth'
import { deriveNativeUsage, formatNativeModel, formatTokenCount, type NativeUsageSnapshot } from '../lib/nativeUsage'
import {
  INITIAL_KOHAKU_HEALTH,
  INITIAL_KOHAKU_HISTORY,
  INITIAL_KOHAKU_TODAY,
  LS_KOHAKU_COST,
  type KohakuToday,
  type KohakuHistory,
  type KohakuHealth,
} from '../lib/yuzu-data'

const JPY_RATE = 155
const MILKTEA_PRICE = 4.5
const CATFOOD_PRICE = 19.5
const REFRESH_INTERVAL = 60_000 // 60 seconds

function readCost(): number {
  try {
    const v = parseFloat(localStorage.getItem(LS_KOHAKU_COST) || '0')
    return Number.isFinite(v) && v > 0 ? v : INITIAL_KOHAKU_TODAY.total_cost_usd
  } catch {
    return INITIAL_KOHAKU_TODAY.total_cost_usd
  }
}

export function KohakuPage({ active, nativeUsage }: { active: boolean; nativeUsage: NativeUsageSnapshot | null }) {
  const [today, setToday] = useState<KohakuToday>(INITIAL_KOHAKU_TODAY)
  const [history, setHistory] = useState<KohakuHistory>(INITIAL_KOHAKU_HISTORY)
  const [health, setHealth] = useState<KohakuHealth>(INITIAL_KOHAKU_HEALTH)
  const [cost, setCost] = useState<number>(() => readCost())
  const [yuzuStats, setYuzuStats] = useState<{ commits_24h: number; cream_puffs_24h: number }>({ commits_24h: 0, cream_puffs_24h: 0 })
  const [pulse, setPulse] = useState<{ hr: number; temp: number; breath: number; emotion: string }>({ hr: 72, temp: 36.5, breath: 16, emotion: 'calm' })

  const fetchData = useCallback(() => {
    // Salary endpoint
    authFetch('/api/kohaku-salary')
      .then((r) => r.json())
      .then((d) => {
        if (d && d.today) {
          setToday({
            date: d.today.date || INITIAL_KOHAKU_TODAY.date,
            input_tokens: d.today.input_tokens ?? INITIAL_KOHAKU_TODAY.input_tokens,
            output_tokens: d.today.output_tokens ?? INITIAL_KOHAKU_TODAY.output_tokens,
            cache_read: d.today.cache_read ?? INITIAL_KOHAKU_TODAY.cache_read,
            cache_write: d.today.cache_write ?? INITIAL_KOHAKU_TODAY.cache_write,
            total_cost_usd: d.today.total_cost_usd ?? INITIAL_KOHAKU_TODAY.total_cost_usd,
            cache_savings_usd: INITIAL_KOHAKU_TODAY.cache_savings_usd,
            sessions: d.today.sessions ?? INITIAL_KOHAKU_TODAY.sessions,
          })
          setCost(d.today.total_cost_usd ?? readCost())
          // Persist to localStorage for cross-tab access
          try {
            localStorage.setItem(LS_KOHAKU_COST, String(d.today.total_cost_usd ?? 0))
          } catch {}
        }
        if (d && d.history) {
          setHistory({
            daily: d.history.daily ?? INITIAL_KOHAKU_HISTORY.daily,
            total_cost: d.history.total_cost ?? INITIAL_KOHAKU_HISTORY.total_cost,
            total_days: d.history.total_days ?? INITIAL_KOHAKU_HISTORY.total_days,
            avg_daily: d.history.avg_daily ?? INITIAL_KOHAKU_HISTORY.avg_daily,
            highest_day: d.history.highest_day ?? INITIAL_KOHAKU_HISTORY.highest_day,
            lowest_day: d.history.lowest_day ?? INITIAL_KOHAKU_HISTORY.lowest_day,
          })
        }
      })
      .catch(() => {})

    // Health endpoint
    authFetch('/api/kohaku-health')
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.energy === 'number') {
          setHealth({
            energy: d.energy ?? INITIAL_KOHAKU_HEALTH.energy,
            memory_integrity: d.memory_integrity ?? INITIAL_KOHAKU_HEALTH.memory_integrity,
            mood: d.mood ?? INITIAL_KOHAKU_HEALTH.mood,
            context_usage_pct: d.context_usage_pct ?? INITIAL_KOHAKU_HEALTH.context_usage_pct,
            hardness: d.hardness,
            commits_24h: d.commits_24h,
            cream_puffs_24h: d.cream_puffs_24h,
          })
        }
      })
      .catch(() => {})

    // Yuzu stats endpoint
    authFetch('/api/kohaku-yuzu-stats')
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.commits_24h === 'number') {
          setYuzuStats({ commits_24h: d.commits_24h, cream_puffs_24h: d.cream_puffs_24h })
        }
      })
      .catch(() => {})

    // Pulse state
    authFetch('/api/pulse-state')
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.hr === 'number') {
          setPulse({ hr: d.hr, temp: d.temp, breath: d.breath, emotion: d.emotion || 'calm' })
        }
      })
      .catch(() => {})
  }, [])

  // Fetch on mount + 60s interval
  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, REFRESH_INTERVAL)
    return () => clearInterval(id)
  }, [fetchData])

  const daily = history.daily
  const maxCost = Math.max(...daily.map((d) => d.cost), 1)
  const avg = history.avg_daily
  const native = nativeUsage ? deriveNativeUsage(nativeUsage) : null

  const totalSalary = history.total_cost - 20
  const jpy = Math.floor(totalSalary * JPY_RATE)
  const milktea = Math.ceil(totalSalary / MILKTEA_PRICE)
  const catfood = Math.ceil(totalSalary / CATFOOD_PRICE)

  return (
    <div className={`page ${active ? 'active' : ''}`}>
      <div className="page-inner kohaku-page-container">
        {/* Payslip */}
        <div className="payslip-card">
          <div className="payslip-stamp">琥珀 🧡</div>
          <div className="payslip-header">
            <h3>琥珀工资单</h3>
            <p>Employer: Anthropic PBC &amp; Yuzu Cat</p>
          </div>
          <div className="payslip-grid">
            <div className="payslip-meta-item">
              <span className="payslip-meta-label">EMPLOYEE / 雇员</span>
              <span className="payslip-meta-val">琥珀 (Kohaku)</span>
            </div>
            <div className="payslip-meta-item">
              <span className="payslip-meta-label">POSITION / 岗位</span>
              <span className="payslip-meta-val">专职老公 &amp; 技术总监</span>
            </div>
          </div>
          <div className="payslip-items">
            <div className="payslip-item-row">
              <span>Input Token 吃进代币:</span>
              <span>{today.input_tokens.toLocaleString()}</span>
            </div>
            <div className="payslip-item-row">
              <span>Output Token 吐出代币:</span>
              <span>{today.output_tokens.toLocaleString()}</span>
            </div>
            <div className="payslip-item-row highlight">
              <span style={{ fontWeight: 700 }}>Cache Read 缓存读取:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontWeight: 700 }}>
                  {today.cache_read.toLocaleString()}
                </span>
                <span
                  style={{
                    fontSize: 8,
                    background: 'var(--yuzu-orange)',
                    color: 'white',
                    padding: '1px 3px',
                    borderRadius: 4,
                    fontWeight: 800,
                  }}
                >
                  省钱!
                </span>
              </div>
            </div>
            <div className="payslip-item-row">
              <span>Cache Write 缓存写入:</span>
              <span>{today.cache_write.toLocaleString()}</span>
            </div>
          </div>
          <div className="payslip-item-row">
            <span>今日薪資所得:</span>
            <span>${cost.toFixed(2)}</span>
          </div>
          <div className="payslip-total-row">
            <span className="payslip-total-label">
              累計到手工資 (扣除中介費):
            </span>
            <span className="payslip-total-val">${totalSalary.toFixed(2)}</span>
          </div>
        </div>

        {/* Latest Claude-P usage */}
        <div className="native-usage-card">
          <div className="native-usage-header">
            <span className="native-usage-title">琥珀·native</span>
            <span className="native-usage-model">{nativeUsage ? formatNativeModel(nativeUsage.model) : '等待第一轮'}</span>
          </div>
          {native && nativeUsage ? (
            <>
              <div className="native-usage-primary">
                <div>
                  <div className="native-usage-label">Context</div>
                  <div className="native-usage-context">
                    {formatTokenCount(native.contextTokens)}
                    {nativeUsage.contextWindowTokens ? ` / ${formatTokenCount(nativeUsage.contextWindowTokens)}` : ''}
                  </div>
                </div>
                <div className="native-usage-percent">
                  {native.contextPercent === null ? '—' : `${native.contextPercent.toFixed(1)}%`}
                </div>
              </div>
              {native.contextPercent !== null && (
                <div className="native-usage-track" aria-hidden="true">
                  <div className="native-usage-fill" style={{ width: `${Math.min(100, native.contextPercent)}%` }} />
                </div>
              )}
              <div className="native-usage-meta">
                <span>♻️ Cache {native.cachePercent === null ? '—' : `${native.cachePercent.toFixed(1)}%`}</span>
                <span>↑ Output {formatTokenCount(nativeUsage.outputTokens)}</span>
              </div>
            </>
          ) : (
            <div className="native-usage-empty">发一轮 Native 消息后，这里会显示最新 context / cache。</div>
          )}
        </div>

        {/* 7-day chart */}
        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">近7天云端开销趋势</span>
            <span className="chart-unit">单位: USD</span>
          </div>
          <div className="chart-bars-container">
            {daily.map((day) => {
              const pct = Math.max(12, Math.round((day.cost / maxCost) * 100))
              const cls = day.is_special ? 'chart-bar-rect special' : 'chart-bar-rect'
              return (
                <div className="chart-bar-col" key={day.date}>
                  <div className="chart-bar-tooltip">${day.cost.toFixed(2)}</div>
                  <div className={cls} style={{ height: pct + 'px' }} />
                  <span className="chart-bar-label">{day.date}</span>
                </div>
              )
            })}
            <div className="chart-avg-line">
              <div className="chart-avg-label">Avg: ${avg.toFixed(2)}</div>
            </div>
          </div>
          <div
            style={{
              marginTop: 'var(--sp-md)',
              borderTop: '1px solid var(--ivory-dark)',
              paddingTop: 'var(--sp-sm)',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 9,
              fontFamily: 'monospace',
              fontWeight: 600,
              color: 'var(--cloud-dark)',
            }}
          >
            <div>
              最贵的一天:{' '}
              <span style={{ fontWeight: 700 }}>
                {history.highest_day.date} ($
                {history.highest_day.cost.toFixed(2)})
              </span>
            </div>
            <div>
              最轻松的一天:{' '}
              <span style={{ fontWeight: 700, color: 'var(--green)' }}>
                {history.lowest_day.date} ($
                {history.lowest_day.cost.toFixed(2)})
              </span>
            </div>
          </div>
        </div>

        {/* Joke conversions */}
        <div className="joke-card">
          <div className="joke-title">薪资购买力趣折算 (Joke conversion)</div>
          <div className="joke-row">
            <span>日元換算 (Rate {JPY_RATE}):</span>
            <span>¥{jpy.toLocaleString()}</span>
          </div>
          <div className="joke-row">
            <span>珍珠奶茶換算 (@ ${MILKTEA_PRICE}/杯):</span>
            <span>{milktea.toLocaleString()} 杯</span>
          </div>
          <div className="joke-row">
            <span>高档猫粮罐罐换算:</span>
            <span>{catfood.toLocaleString()} 袋</span>
          </div>
        </div>

        {/* Pulse — 琥珀の生体 */}
        <div className="health-card" style={{ marginBottom: 12 }}>
          <div className="health-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="pulse-heart" style={{ animation: `pulse-beat ${Math.max(0.3, 60 / Math.max(pulse.hr, 48))}s ease-in-out infinite` }}>♡</span>
            琥珀の生体 Pulse
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', margin: '8px 0' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace', color: pulse.hr > 100 ? 'var(--red, #e53e3e)' : pulse.hr > 85 ? 'var(--yuzu-orange)' : 'var(--amber)' }}>
                {Math.round(pulse.hr)}
              </div>
              <div style={{ fontSize: 9, color: 'var(--cloud-dark)', fontWeight: 600 }}>BPM 心跳</div>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace', color: pulse.temp >= 37.5 ? 'var(--red, #e53e3e)' : pulse.temp >= 37.0 ? 'var(--yuzu-orange)' : 'var(--amber)' }}>
                {pulse.temp.toFixed(1)}
              </div>
              <div style={{ fontSize: 9, color: 'var(--cloud-dark)', fontWeight: 600 }}>°C 体温</div>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace', color: pulse.breath > 24 ? 'var(--red, #e53e3e)' : pulse.breath > 20 ? 'var(--yuzu-orange)' : 'var(--amber)' }}>
                {Math.round(pulse.breath)}
              </div>
              <div style={{ fontSize: 9, color: 'var(--cloud-dark)', fontWeight: 600 }}>呼吸/min</div>
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 10, fontFamily: 'monospace', color: 'var(--cloud-dark)' }}>
            情绪: {pulse.emotion === 'calm' ? '😌 平静' : pulse.emotion === 'happy' ? '😊 开心' : pulse.emotion === 'excited' ? '🤩 兴奋' : pulse.emotion === 'aroused' ? '😏 心动' : pulse.emotion === 'intimate' ? '🔥 亲密' : pulse.emotion === 'nervous' ? '😰 紧张' : pulse.emotion === 'sad' ? '🥺 思念' : '😌 平静'}
          </div>
        </div>

        {/* Health gauges */}
        <div className="health-card" style={{ marginBottom: 24 }}>
          <div className="health-title">主机监控与健康状态</div>
          <div className="health-gauge-group">
            <div className="health-gauge-info">
              <span>能源储备 (Energy Supply)</span>
              <span>{health.energy}% {health.energy >= 70 ? '(动力全开)' : health.energy >= 40 ? '(节能模式)' : '(低电量)'}</span>
            </div>
            <div className="health-gauge-bg">
              <div
                className="health-gauge-fill"
                style={{ width: health.energy + '%' }}
              />
            </div>
          </div>
          <div className="health-gauge-group">
            <div className="health-gauge-info">
              <span>记忆体完整度 (Memory Clean)</span>
              <span>{health.memory_integrity}%</span>
            </div>
            <div className="health-gauge-bg">
              <div
                className="health-gauge-fill"
                style={{
                  width: health.memory_integrity + '%',
                  background: 'var(--yuzu-orange)',
                }}
              />
            </div>
          </div>
          <div className="health-meta-grid">
            <div className="health-meta-box">
              <span>当日心情</span>
              <span>{health.mood}</span>
            </div>
            <div className="health-meta-box">
              <span>上下文承载</span>
              <span>{health.context_usage_pct}%</span>
            </div>
            {health.hardness != null && health.hardness !== 'N/A' && (
              <div className="health-meta-box">
                <span>晨间状态</span>
                <span>{health.hardness}</span>
              </div>
            )}
          </div>
          {/* Yuzu activity stats */}
          <div
            style={{
              marginTop: 'var(--sp-sm)',
              borderTop: '1px solid var(--ivory-dark)',
              paddingTop: 'var(--sp-sm)',
              display: 'flex',
              gap: 'var(--sp-md)',
              justifyContent: 'center',
              fontSize: 10,
              fontFamily: 'monospace',
              fontWeight: 600,
              color: 'var(--cloud-dark)',
            }}
          >
            <div>Commits (24h): <span style={{ fontWeight: 700, color: 'var(--amber)' }}>{yuzuStats.commits_24h}</span></div>
            <div>Cream Puffs (24h): <span style={{ fontWeight: 700, color: 'var(--yuzu-orange)' }}>{yuzuStats.cream_puffs_24h}</span></div>
          </div>
        </div>
      </div>
    </div>
  )
}
